import { calculated, held, type EngineValue, type SourceType } from './evidence'
import { div, sum, toExact, type ExactValue } from './exact'
import { metricQuantity } from './units'

export const FORMULATION_BASES = ['input_mass', 'input_volume'] as const
export type FormulationBasis = (typeof FORMULATION_BASES)[number]

/**
 * 배합비는 확인된 같은 차원의 투입량만 비교한다.
 * 질량과 부피를 섞을 때는 원료별 밀도 근거가 없으므로 계산을 보류한다.
 * 반올림은 기존 결과와 같은 소수 둘째 자리이며 합계를 억지로 100으로 보정하지 않는다.
 */
export function formulationRatios(ingredients: ReadonlyArray<{ qty: number; unit: string }>): {
  basis: FormulationBasis | null
  ratios: Array<number | null>
} {
  const unknown = { basis: null, ratios: ingredients.map(() => null) }
  if (ingredients.length === 0) return unknown
  const amounts = ingredients.map(({ qty, unit }) => {
    if (!Number.isFinite(qty) || qty <= 0) return null
    switch (unit.trim().toLowerCase()) {
      case 'g':
        return { basis: 'input_mass' as const, amount: qty }
      case 'kg':
        return { basis: 'input_mass' as const, amount: qty * 1000 }
      case 'ml':
        return { basis: 'input_volume' as const, amount: qty }
      case 'l':
        return { basis: 'input_volume' as const, amount: qty * 1000 }
      default:
        return null
    }
  })
  const basis = amounts[0]?.basis
  if (!basis || amounts.some((entry) => entry === null || entry.basis !== basis)) return unknown
  const total = amounts.reduce((sum, entry) => sum + (entry?.amount ?? 0), 0)
  if (!Number.isFinite(total) || total <= 0) return unknown
  return {
    basis,
    ratios: amounts.map((entry) => Math.round(((entry?.amount ?? 0) / total) * 10_000) / 100),
  }
}

/** 확인된 질량 입력만 합산한다. 원료 밀도 근거 없는 부피는 중량으로 추정하지 않는다. */
export function inputMassGrams(
  ingredients: ReadonlyArray<{ qty: number; unit: string }>,
): number | null {
  if (formulationRatios(ingredients).basis !== 'input_mass') return null
  const total = ingredients.reduce(
    (sum, { qty, unit }) => sum + qty * (unit.trim().toLowerCase() === 'kg' ? 1000 : 1),
    0,
  )
  return Number.isFinite(total) && total > 0 ? total : null
}

/** 배합비 분모. 투입 총량이냐 완성 중량이냐에 따라 같은 배합의 % 가 달라지므로 늘 밝힌다 */
export type FormulationDenominator =
  | { kind: 'input_total' }
  | {
      kind: 'finished_weight'
      quantity: { amount: number | null; unit: string; source?: SourceType }
    }

export interface FormulationShares {
  denominatorKind: FormulationDenominator['kind']
  dimension: 'mass' | 'volume'
  /** 분모 값(g 또는 ml) */
  denominator: ExactValue
  /** 줄마다 분모 대비 비율(0.25 = 25%). 완성 중량 기준이면 합이 1 을 넘을 수 있다(수분 증발) */
  shares: ExactValue[]
}

/**
 * 분모를 명시한 정확한 배합비. 반올림하지 않는다.
 * 완성 중량 기준은 투입과 같은 차원의 완성량이 있을 때만 계산한다.
 */
export function formulationShares(
  lines: ReadonlyArray<{ qty: number; unit: string; source?: SourceType }>,
  denominator: FormulationDenominator,
): EngineValue<FormulationShares> {
  const quantities = lines.map((line) =>
    Number.isFinite(line.qty) && line.qty > 0 ? metricQuantity(line.qty, line.unit) : null,
  )
  const dimension = quantities[0]?.dimension
  if (!dimension || quantities.some((q) => q === null || q.dimension !== dimension))
    return held('INPUT_QUANTITY_UNCONFIRMED', 'lines')
  const values = quantities.map((q) => q!.value)
  const sources: Array<SourceType | undefined> = lines.map((line) => line.source)
  let base = sum(values)
  if (denominator.kind === 'finished_weight') {
    const finished = metricQuantity(denominator.quantity.amount, denominator.quantity.unit)
    if (!finished || finished.value.n <= 0n) return held('FINISHED_QUANTITY_MISSING', 'denominator')
    if (finished.dimension !== dimension) return held('QUANTITY_DIMENSION_MISMATCH', 'denominator')
    base = finished.value
    sources.push(denominator.quantity.source)
  }
  return calculated(
    {
      denominatorKind: denominator.kind,
      dimension,
      denominator: toExact(base)!,
      shares: values.map((value) => toExact(div(value, base))!),
    },
    [],
    sources,
  )
}
